import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaCamera } from 'react-icons/fa6';

const FaceAuth = ({ onFaceData, token }) => {
  const videoRef = useRef();
  const canvasRef = useRef();
  const [isFaceAuthActive, setIsFaceAuthActive] = useState(false);
  const [facePrompt, setFacePrompt] = useState(null);
  const [faceAuthError, setFaceAuthError] = useState(null);
  const [faceData, setFaceData] = useState(null);

  useEffect(() => {
    const startWebcam = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing webcam:', err);
        setFaceAuthError('Please allow webcam access to proceed');
        toast.error('Please allow webcam access to proceed');
      }
    };

    if (isFaceAuthActive) {
      startWebcam();
    }

    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isFaceAuthActive]);

  const handleFaceAuth = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setFaceAuthError(null);
    const prompts = ['open_mouth', 'head_shake'];
    const selectedPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    setFacePrompt(selectedPrompt.replace('_', ' '));

    try {
      const canvas = canvasRef.current;
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}facepp/detect`,
        { imageBase64, livenessAction: selectedPrompt },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { faceToken, attributes, isLivenessValid } = data;

      if (!isLivenessValid) {
        setFaceAuthError(`Please ${selectedPrompt.replace('_', ' ')}`);
        toast.error(`Please ${selectedPrompt.replace('_', ' ')}`);
        return;
      }

      const newFaceData = { faceToken, attributes };
      setFaceData(newFaceData);
      onFaceData(newFaceData); // Pass faceData to parent
      toast.success('Face authentication successful!');
      setIsFaceAuthActive(false);
    } catch (err) {
      console.error('Face++ auth error:', err.response?.data || err.message);
      setFaceAuthError('Error processing face data');
      // toast.error('Error processing face data');
    }
  };

  return (
    <div className="relative flex flex-col items-center">
      {isFaceAuthActive ? (
        <>
          <video
            ref={videoRef}
            autoPlay
            className="w-32 h-32 rounded-full object-cover border-4 border-gray-100 shadow-sm"
          />
          <canvas ref={canvasRef} className="hidden" />
          <p className="mt-2 text-sm text-gray-600">{facePrompt || 'Please follow the prompt'}</p>
          {faceAuthError && <p className="mt-2 text-sm text-red-500">{faceAuthError}</p>}
          <button
            onClick={handleFaceAuth}
            className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Capture Face
          </button>
          <button
            onClick={() => {
              setIsFaceAuthActive(false);
              if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
              }
            }}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          onClick={() => setIsFaceAuthActive(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Start Face Authentication
        </button>
      )}
      {faceData && (
        <p className="mt-2 text-sm text-green-600">Face data captured! Will be saved with profile update.</p>
      )}
    </div>
  );
};

export default FaceAuth;